import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, resolveAsset } from '../api/client';
import { useAuth } from '../state/auth';
import BookViewer, { buildFullBookPages } from '../components/BookViewer';
import BookDownload from '../components/BookDownload';
import FinalizeLoader from '../components/FinalizeLoader';
import ShippingAddressFields from '../components/ShippingAddressFields';
import { useT, useLocalField } from '../i18n';

// Mirror of the server guard: address is editable until the order enters the
// shipping pipeline (server/routes/orders.js SHIPPING_LOCKED_STATUSES).
const SHIPPING_LOCKED_STATUSES = ['shipped', 'delivered', 'refunded', 'cancelled'];

// Admin-only: render everything the customer entered in the create form, plus
// the uploaded images (child photo + reference images). Image-URL keys are
// pulled out and shown as thumbnails; every other field is shown label/value.
function RequestDetails({ personalization: p }) {
  const t = useT();

  // Translated labels for the personalization fields the customer filled in.
  // Unknown keys fall back to the raw key so nothing is ever hidden from the admin.
  const REQUEST_FIELD_LABELS = useMemo(() => ({
    name: t.orderRequestFieldName,
    age: t.orderRequestFieldAge,
    gender: t.orderRequestFieldGender,
    issue: t.orderRequestFieldIssue,
    customFear: t.orderRequestFieldCustomFear,
    whenHappens: t.orderRequestFieldWhenHappens,
    howBehaves: t.orderRequestFieldHowBehaves,
    smallGoal: t.orderRequestFieldSmallGoal,
    familyStructure: t.orderRequestFieldFamilyStructure,
    personalityTraits: t.orderRequestFieldPersonalityTraits,
    interests: t.orderRequestFieldInterests,
    environment: t.orderRequestFieldEnvironment,
    storyLanguage: t.orderRequestFieldStoryLanguage,
    imageStyle: t.orderRequestFieldImageStyle,
    dialect: t.orderRequestFieldDialect,
    variantSku: t.orderRequestFieldVariantSku,
  }), [t]);

  if (!p || typeof p !== 'object') return <p className="empty-state">{t.orderRequestNoDetails}</p>;

  const imageKeys = new Set(['characterImageUrl', 'referenceImageUrls', 'referenceUrls']);
  const rows = Object.entries(p).filter(
    ([k, v]) => !imageKeys.has(k) && v != null && v !== '' && !(Array.isArray(v) && v.length === 0)
  );

  const charImg = typeof p.characterImageUrl === 'string' ? p.characterImageUrl : null;
  const refImgs = Array.isArray(p.referenceImageUrls) ? p.referenceImageUrls : [];
  const typedRefs = String(p.referenceUrls || '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => /^https?:\/\//i.test(s));
  const allRefs = [...refImgs, ...typedRefs];

  return (
    <div className="request-details-body">
      <dl className="request-details-list">
        {rows.map(([k, v]) => (
          <div key={k} className="request-details-row">
            <dt>{REQUEST_FIELD_LABELS[k] || k}</dt>
            <dd>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
          </div>
        ))}
      </dl>

      {(charImg || allRefs.length > 0) && (
        <div className="request-details-images">
          <h4>{t.orderUploadedImages}</h4>
          <div className="request-details-thumbs">
            {charImg && (
              <a href={resolveAsset(charImg)} target="_blank" rel="noopener noreferrer" className="request-thumb">
                <img src={resolveAsset(charImg)} alt={t.orderChildPhoto} />
                <span>{t.orderChildPhoto}</span>
              </a>
            )}
            {allRefs.map((url, i) => (
              <a key={i} href={resolveAsset(url)} target="_blank" rel="noopener noreferrer" className="request-thumb">
                <img src={resolveAsset(url)} alt={`${t.orderReferenceImage} ${i + 1}`} />
                <span>{t.orderReferenceImage} {i + 1}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// "Ask us to edit" box — the customer describes changes to a created story and we
// refine it before finalizing for print. Posts to the order and emails the team.
function RequestEdits({ orderId, shareToken }) {
  const t = useT();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!text.trim()) { setErr(t.requestEditsEmpty); return; }
    setSending(true);
    setErr('');
    try {
      await api.post(`/api/orders/${orderId}/request-changes`, { text: text.trim(), shareToken });
      setSent(true);
      setText('');
    } catch (e) {
      setErr(e.message);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return <p className="finalize-approved-inline">{t.requestEditsSent}</p>;
  }

  return (
    <div className="payfirst-request-edits">
      <label className="field">
        <span className="field-label">{t.requestEditsTitle}</span>
        <textarea
          className="finalize-textarea"
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t.requestEditsPlaceholder}
        />
      </label>
      {err && <p className="auth-error">{err}</p>}
      <button type="button" className="ghost-btn" onClick={submit} disabled={sending}>
        {sending ? t.requestEditsSending : t.requestEditsSubmit}
      </button>
    </div>
  );
}

export default function OrderDetail() {
  const t = useT();
  const lf = useLocalField();
  const statusLabels = useMemo(() => ({
    pending_payment: t.status_pending_payment,
    paid: t.status_paid,
    in_production: t.status_in_production,
    shipped: t.status_shipped,
    delivered: t.status_delivered,
  }), [t]);
  const { id } = useParams();
  const { isStaff } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [story, setStory] = useState(null);
  const [bookLoading, setBookLoading] = useState(false);
  const [bookFinalizing, setBookFinalizing] = useState(false);
  const [bookError, setBookError] = useState('');
  const [editingAddress, setEditingAddress] = useState(false);
  const [addrDraft, setAddrDraft] = useState(null);
  const [savingAddr, setSavingAddr] = useState(false);
  const [addrError, setAddrError] = useState('');
  const [requestDetails, setRequestDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  // storyId currently rendering its print PDF (empty when idle). The render is a
  // multi-second headless-Chrome job, so the button shows a busy label.
  const [printBusy, setPrintBusy] = useState('');
  // storyId currently being assigned a tracking barcode (empty when idle).
  const [barcodeBusy, setBarcodeBusy] = useState('');

  useEffect(() => {
    api.get(`/api/orders/${id}`)
      .then(setOrder)
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [id]);

  const pages = useMemo(() => (story ? buildFullBookPages(story, t) : []), [story, t]);

  // Lazy-load the finished book on demand. Heavy illustration generation can
  // take 30-120s, so we never fire it on page load — only when the buyer or an
  // admin explicitly asks to view/download. finalize-illustrations is gated
  // server-side on a paid Order existing, so it's a no-op for unpaid orders.
  const loadBook = async (shareToken) => {
    if (!shareToken || bookLoading || bookFinalizing) return;
    setBookError('');
    setBookLoading(true);
    try {
      let data = await api.get(`/api/stories/preview/${shareToken}`);
      setStory(data);
      const needsFinalize = (data?.scenes || []).some((s) => !s.imageUrl);
      if (needsFinalize) {
        setBookLoading(false);
        setBookFinalizing(true);
        await api.post(`/api/stories/${shareToken}/finalize-illustrations`, {});
        data = await api.get(`/api/stories/preview/${shareToken}`);
        setStory(data);
      }
    } catch (e) {
      setBookError(e.message);
    } finally {
      setBookLoading(false);
      setBookFinalizing(false);
    }
  };

  // Admin-only: load the personalization the customer submitted (all form
  // fields + uploaded image URLs). Reuses an already-loaded story; otherwise
  // fetches the preview WITHOUT triggering illustration finalization.
  const loadRequestDetails = async (token) => {
    if (!token || detailsLoading) return;
    if (story?.personalization) { setRequestDetails(story.personalization); return; }
    setDetailsError('');
    setDetailsLoading(true);
    try {
      const d = await api.get(`/api/stories/preview/${token}`);
      setRequestDetails(d.personalization || {});
    } catch (e) {
      setDetailsError(e.message);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Admin: download the print-house (page-by-page) PDF. Fetch → blob → click a
  // hidden <a download> so it actually saves a file (a plain link just opens the
  // bytes in a new tab and, on a GCS redirect, displays them inline). fetch
  // follows the endpoint's 302-to-GCS (the bucket allows CORS) and surfaces a
  // 403/404/500 as an alert instead of a blank tab. Mirrors BookDownload.
  // Download ONE print-house file: part='cover' (the wraparound case wrap) or
  // part='pages' (the interior, page-by-page). One button each.
  const downloadPrintPdf = async (it, part) => {
    const token = it.storyId?.shareToken;
    if (!token || printBusy) return;
    setPrintBusy(`${it.storyId._id}:${part}`);
    try {
      const base = lf(it.templateId || {}, 'nameAr') || order.orderNumber || 'book';
      const safe = String(base).replace(/[\\/:*?"<>|]/g, '').trim().slice(0, 80) || 'book';
      const suffix = part === 'cover' ? 'cover' : 'pages';
      // Stamp the tracking serial into the filename so the printer can correlate
      // the file at a glance: e.g. "اسم_cover_AB-00042.pdf".
      const bc = (it.storyId?.barcode || '').trim();
      const res = await fetch(resolveAsset(`/api/stories/${token}/book-print.pdf?part=${part}`));
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safe}_${suffix}${bc ? `_${bc}` : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(t.orderPrintPdfError + (err?.message || t.orderPrintPdfErrorUnknown));
    } finally {
      setPrintBusy('');
    }
  };

  // Admin: assign the internal tracking barcode (serial like AB-00042) to this
  // story. Server-minted + idempotent. Covers the "PDF buyer now wants to print"
  // case; printed orders get one automatically when paid. On success we patch the
  // order in state so the serial shows immediately.
  const assignBarcode = async (it) => {
    const storyId = it.storyId?._id;
    if (!storyId || barcodeBusy) return;
    setBarcodeBusy(storyId);
    try {
      const res = await api.post(`/api/admin/stories/${storyId}/barcode`, {});
      setOrder((prev) => prev && ({
        ...prev,
        items: prev.items.map((item) =>
          item.storyId?._id === storyId
            ? { ...item, storyId: { ...item.storyId, barcode: res.barcode } }
            : item),
      }));
    } catch (err) {
      alert(t.orderBarcodeError + (err?.message || t.orderPrintPdfErrorUnknown));
    } finally {
      setBarcodeBusy('');
    }
  };

  const startEditAddress = () => {
    const s = order.shippingAddress || {};
    setAddrDraft({
      fullName: s.fullName || '', phone: s.phone || '', city: s.city || '',
      neighborhood: s.neighborhood || '', address: s.address || '',
      landmark: s.landmark || '', postalCode: s.postalCode || '',
    });
    setAddrError('');
    setEditingAddress(true);
  };

  const saveAddress = async () => {
    setSavingAddr(true);
    setAddrError('');
    try {
      const updated = await api.put(`/api/orders/${id}/shipping-address`, addrDraft);
      setOrder(updated);
      setEditingAddress(false);
    } catch (e) {
      setAddrError(e.message);
    } finally {
      setSavingAddr(false);
    }
  };

  if (loading) return <div className="loader"><div className="loader-spinner" /></div>;
  if (!order) return (
    <div className="error-card">
      <h2>{t.templateNotFound}</h2>
      <Link to="/" className="submit-btn">{t.backToHome}</Link>
    </div>
  );

  const variantLabels = { softcover: t.softcover, hardcover: t.hardcover, digital: t.digital };
  // A PDF-only order has nothing to send to print, so instead of the finalize/
  // approve-printing banner it gets an "upgrade to printed book" CTA (pay the
  // difference). Hidden once a paid upgrade order exists (upgradeOrderId).
  const isPdfOnlyOrder = (order.items || []).length > 0 && order.items.every((it) => it.type === 'digital');
  // Pay-first orders drive their own "create your stories" section below, so the
  // generic finalize/upgrade banners and the single-book viewer are suppressed.
  const isPayFirst = !!order.payFirst;
  const showUpgradeBanner = isPdfOnlyOrder && order.status === 'paid' && !order.upgradeOrderId && !isPayFirst;
  const showFinalizeBanner = order.status === 'paid' && !order.approvedAt && !isPdfOnlyOrder && !isPayFirst;
  const isPaid = order.status !== 'pending_payment';
  const shareToken = order.items?.find((it) => it.storyId?.shareToken)?.storyId?.shareToken;
  // Story items the admin can jump into the scene editor for (one per book).
  const storyItems = (order.items || []).filter((it) => it.storyId?._id);
  const addr = order.shippingAddress || {};
  const hasShipping = !!addr.fullName;
  const canEditAddress = hasShipping && !SHIPPING_LOCKED_STATUSES.includes(order.status);

  return (
    <div className="order-detail">
      <h2>{t.orderTitle}</h2>

      {order.status === 'pending_payment' && shareToken && (
        <Link to={`/checkout/${shareToken}`} className="finalize-banner">
          <div className="finalize-banner-text">
            <strong>{t.completePaymentTitle}</strong>
            <span>{t.completePaymentDesc}</span>
          </div>
          <span className="finalize-banner-cta">{t.completePayment} ←</span>
        </Link>
      )}

      {showFinalizeBanner && (
        <Link to={`/account/orders/${order._id}/finalize`} className="finalize-banner">
          <div className="finalize-banner-text">
            <strong>{t.finalizeBannerTitle}</strong>
            <span>{t.finalizeBannerDesc}</span>
          </div>
          <span className="finalize-banner-cta">{t.finalizeBannerCta} ←</span>
        </Link>
      )}

      {showUpgradeBanner && (
        <Link to={`/account/orders/${order._id}/upgrade`} className="finalize-banner">
          <div className="finalize-banner-text">
            <strong>{t.upgradeBannerTitle}</strong>
            <span>{t.upgradeBannerDesc}</span>
          </div>
          <span className="finalize-banner-cta">{t.upgradeBannerCta} ←</span>
        </Link>
      )}

      {isPdfOnlyOrder && order.upgradeOrderId && (
        <div className="finalize-approved-card">
          <div className="finalize-approved-icon">✓</div>
          <div>
            <strong>{t.upgradeDoneTitle}</strong>
            <p>{t.upgradeDoneDesc}</p>
            <Link to={`/account/orders/${order.upgradeOrderId}`}>{t.upgradeDoneLink} ←</Link>
          </div>
        </div>
      )}

      {order.approvedAt && (
        <div className="finalize-approved-card">
          <div className="finalize-approved-icon">✓</div>
          <div>
            <strong>{t.finalizeApprovedTitle}</strong>
            <p>{t.finalizeApprovedDesc}</p>
            <p className="finalize-approved-date">
              {t.finalizeApprovedOn}: {new Date(order.approvedAt).toLocaleDateString('ar')}
            </p>
          </div>
        </div>
      )}

      {isPayFirst && isPaid && (
        <div className="payfirst-create-section">
          <h3>{t.payFirstOrderCreateTitle}</h3>
          <p className="order-book-hint">{t.payFirstOrderCreateHint}</p>
          <div className="payfirst-create-list">
            {order.items.map((item, i) => {
              const created = item.storyId?.shareToken;
              const generating = item.storyId && !item.storyId.shareToken; // linked but token not populated (rare)
              return (
                <div key={i} className="payfirst-create-item">
                  <div className="payfirst-create-item-head">
                    <span className="order-item-name">
                      {lf(item.templateId || {}, 'nameAr') || t.cartStoryFallback}
                    </span>
                    <span className="order-item-variant">{variantLabels[item.type] || item.type}</span>
                  </div>
                  {created ? (
                    <div className="payfirst-create-item-actions">
                      <Link to={`/preview/${item.storyId.shareToken}`} className="submit-btn">
                        {t.payFirstStoryCreatedCta}
                      </Link>
                      <RequestEdits orderId={order._id} shareToken={item.storyId.shareToken} />
                    </div>
                  ) : generating ? (
                    <span className="payfirst-story-generating">{t.payFirstStoryGenerating}</span>
                  ) : (
                    <Link to={`/account/orders/${order._id}/create/${i}`} className="submit-btn">
                      {t.payFirstCreateStoryCta}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isStaff && shareToken && (
        <div className="order-admin-tools">
          <div className="order-admin-tools-head">
            <span className="order-admin-tools-tag">{t.orderAdminToolsTag}</span>
            <div className="order-admin-tools-actions">
              {storyItems.map((it) => (
                <Link
                  key={it.storyId._id}
                  to={`/admin/stories/${it.storyId._id}`}
                  className="submit-btn"
                >
                  {t.orderAdminEditScenes}{storyItems.length > 1 && lf(it.templateId || {}, 'nameAr') ? ` — ${lf(it.templateId, 'nameAr')}` : ''}
                </Link>
              ))}
              {/* Print-house PDF: every internal scene split into a separate
                  image page + text page (20×20cm), not the 40×21cm spreads.
                  Gated on a paid order — same as the book-print.pdf endpoint. */}
              {['paid', 'in_production', 'printing', 'shipped', 'delivered'].includes(order.status) &&
                storyItems.map((it) => {
                  if (!it.storyId?.shareToken) return null;
                  const label = storyItems.length > 1 && lf(it.templateId || {}, 'nameAr') ? ` — ${lf(it.templateId, 'nameAr')}` : '';
                  return (
                    <React.Fragment key={`print-${it.storyId._id}`}>
                      <button
                        type="button"
                        className="ghost-btn"
                        onClick={() => downloadPrintPdf(it, 'cover')}
                        disabled={!!printBusy}
                      >
                        {printBusy === `${it.storyId._id}:cover` ? t.orderAdminPdfBusy : `${t.orderAdminPdfCover}${label}`}
                      </button>
                      <button
                        type="button"
                        className="ghost-btn"
                        onClick={() => downloadPrintPdf(it, 'pages')}
                        disabled={!!printBusy}
                      >
                        {printBusy === `${it.storyId._id}:pages` ? t.orderAdminPdfBusy : `${t.orderAdminPdfPages}${label}`}
                      </button>
                    </React.Fragment>
                  );
                })}
              {/* Internal tracking barcode (printed on the back cover + dedication).
                  Auto-assigned when a printed order is paid; assignable here for a
                  PDF buyer who now wants to print. Shows the serial once set. */}
              {storyItems.map((it) => {
                const label = storyItems.length > 1 && lf(it.templateId || {}, 'nameAr') ? ` — ${lf(it.templateId, 'nameAr')}` : '';
                return it.storyId?.barcode ? (
                  <span key={`bc-${it.storyId._id}`} className="order-barcode-serial">
                    🏷️ {t.orderAdminBarcodeLabel}{label}: <strong dir="ltr">{it.storyId.barcode}</strong>
                  </span>
                ) : (
                  <button
                    key={`bc-${it.storyId._id}`}
                    type="button"
                    className="ghost-btn"
                    onClick={() => assignBarcode(it)}
                    disabled={!!barcodeBusy}
                  >
                    {barcodeBusy === it.storyId._id ? t.orderAdminAssigningBarcode : `🏷️ ${t.orderAdminAssignBarcode}${label}`}
                  </button>
                );
              })}
            </div>
          </div>

          <h3>{t.orderAdminRequestTitle}</h3>
          {requestDetails ? (
            <RequestDetails personalization={requestDetails} />
          ) : (
            <div className="order-book-load">
              <p className="order-book-hint">{t.orderAdminRequestHint}</p>
              <button
                type="button"
                className="submit-btn"
                onClick={() => loadRequestDetails(shareToken)}
                disabled={detailsLoading}
              >
                {detailsLoading ? t.orderAdminRequestLoading : t.orderAdminRequestBtn}
              </button>
            </div>
          )}
          {detailsError && <p className="auth-error">{detailsError}</p>}
        </div>
      )}

      <div className="order-detail-grid">
        <div className="order-detail-row">
          <span className="order-detail-label">{t.orderNumber}</span>
          <span>{order.orderNumber}</span>
        </div>
        <div className="order-detail-row">
          <span className="order-detail-label">{t.orderStatus}</span>
          <span className={`order-status-badge status-${order.status}`}>
            {statusLabels[order.status] || order.status}
          </span>
        </div>
        {isStaff && order.lastPaymentError?.at && order.status !== 'paid' && (
          <div className="order-detail-row">
            <span className="order-detail-label">{t.status_payment_failed}</span>
            <span style={{ color: '#b91c1c' }}>
              {order.lastPaymentError.reason || t.status_payment_failed}
              {' · '}
              {new Date(order.lastPaymentError.at).toLocaleString('ar')}
            </span>
          </div>
        )}
        <div className="order-detail-row">
          <span className="order-detail-label">{t.orderDate}</span>
          <span>{new Date(order.createdAt).toLocaleDateString('ar')}</span>
        </div>
        {order.shippingCents > 0 && (
          <div className="order-detail-row">
            <span className="order-detail-label">{t.delivery}</span>
            <span>{(order.shippingCents / 100).toFixed(0)}{t.currency}</span>
          </div>
        )}
        {order.upgradeCreditCents > 0 && (
          <div className="order-detail-row">
            <span className="order-detail-label">{t.upgradeAlreadyPaid}</span>
            <span>−{(order.upgradeCreditCents / 100).toFixed(0)}{t.currency}</span>
          </div>
        )}
        <div className="order-detail-row">
          <span className="order-detail-label">{t.total}</span>
          <span>{(order.totalCents / 100).toFixed(0)}{t.currency}</span>
        </div>
      </div>

      {hasShipping && (
        <div className="order-shipping-section">
          <div className="order-shipping-head">
            <h3>{t.orderShipping}</h3>
            {canEditAddress && !editingAddress && (
              <button type="button" className="link-btn" onClick={startEditAddress}>
                {t.orderShippingEdit}
              </button>
            )}
          </div>

          {editingAddress ? (
            <div className="order-shipping-edit">
              <ShippingAddressFields value={addrDraft} onChange={setAddrDraft} />
              {addrError && <p className="auth-error">{addrError}</p>}
              <div className="order-shipping-actions">
                <button type="button" className="submit-btn" onClick={saveAddress} disabled={savingAddr}>
                  {savingAddr ? t.orderShippingSaving : t.orderShippingSave}
                </button>
                <button type="button" className="ghost-btn" onClick={() => setEditingAddress(false)} disabled={savingAddr}>
                  {t.orderShippingCancel}
                </button>
              </div>
            </div>
          ) : (
            <>
              <p>
                {addr.fullName}<br />
                {addr.address}<br />
                {addr.neighborhood && <>{addr.neighborhood}<br /></>}
                {addr.city}{addr.postalCode ? `، ${addr.postalCode}` : ''}<br />
                {addr.landmark && <>{t.checkoutLandmark}: {addr.landmark}<br /></>}
                {addr.phone}
              </p>
              {!canEditAddress && (
                <p className="order-shipping-locked">{t.orderShippingLocked}</p>
              )}
            </>
          )}
        </div>
      )}

      <div className="order-items-section">
        <h3>{t.orderItems}</h3>
        {order.items.map((item, i) => (
          <div key={i} className="order-item-card">
            <span className="order-item-name">
              {lf(item.templateId || {}, 'nameAr') || t.cartStoryFallback}
            </span>
            <span className="order-item-variant">
              {variantLabels[item.type] || item.type}
            </span>
            {(item.includesPdf || item.type === 'digital') && (
              <span className="pdf-badge">{t.pdfIncludedTag}</span>
            )}
            <span className="order-item-price">
              {(item.priceCents / 100).toFixed(0)}{t.currency}
            </span>
          </div>
        ))}
      </div>

      {isPaid && shareToken && !isPayFirst && (
        <div className="order-book-section">
          <h3>{t.orderBookTitle}</h3>

          {bookFinalizing ? (
            <FinalizeLoader />
          ) : story && pages.length > 0 ? (
            <>
              <BookViewer pages={pages} font={story.storyFont} />
              <div className="order-book-download">
                <BookDownload story={story} />
              </div>
            </>
          ) : (
            <div className="order-book-load">
              <p className="order-book-hint">{t.orderBookHint}</p>
              <button
                type="button"
                className="submit-btn"
                onClick={() => loadBook(shareToken)}
                disabled={bookLoading}
              >
                {bookLoading ? t.orderBookLoading : t.orderBookView}
              </button>
              {bookError && <p className="auth-error">{bookError}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
